import { useWatch } from 'react-hook-form';
import React, { useState } from 'react';
import { Modal, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Close } from '@mui/icons-material';

import { FormInputTextArea } from '../../../../form-components/FormInputTextArea';

import CashPaymentForm from '../../../manualSales/payment/cashPaymentForm';
import SplitPaymentSection from './SplitPaymentSection';

import {
  memoTextAreaStyle,
  ContentSection,
  FormFieldGroup,
  FormFieldLabel,
} from './EnterSaleInfoModalStyled';

// ============================================
// Styled Components
// ============================================
const StyledModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ModalContainer = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '12px',
  width: '480px',
  maxHeight: '80vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  borderBottom: '1px solid #E5E5E5',
});

const ModalTitle = styled('h2')({
  fontSize: '18px',
  fontWeight: 600,
  color: '#111',
  margin: 0,
});

const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.7,
  },
});

const ModalContent = styled(Box)({
  padding: '24px',
  overflowY: 'auto',
  flex: 1,
});

const ModalFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '16px 24px',
  borderTop: '1px solid #E5E5E5',
});

const CancelButton = styled('button')({
  padding: '10px 24px',
  borderRadius: '8px',
  border: '1px solid #E5E5E5',
  backgroundColor: '#fff',
  color: '#666',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
});

const ConfirmButton = styled('button')({
  padding: '10px 24px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#00B4A0',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#009688',
  },
  // 금액 입력이 끝나면 마지막 행동(결제 완료)을 펄스로 안내한다
  '@keyframes confirmGuidePulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(0, 180, 160, 0.55)' },
    '70%': { boxShadow: '0 0 0 9px rgba(0, 180, 160, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(0, 180, 160, 0)' },
  },
  '&.confirm-guide-pulse': {
    animation: 'confirmGuidePulse 1.2s ease-out infinite',
  },
  '&:disabled': {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
});

const SummarySection = styled(Box)({
  backgroundColor: '#F8F9FA',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '20px',
});

const SummaryRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '& + &': {
    marginTop: '8px',
  },
});

const SummaryLabel = styled('span')({
  fontSize: '14px',
  color: '#666',
});

const SummaryValue = styled('span')({
  fontSize: '14px',
  fontWeight: 500,
  color: '#111',
});

const SummaryTotal = styled('span')({
  fontSize: '18px',
  fontWeight: 700,
  color: '#00B4A0',
});

// ============================================
// Component
// ============================================
const PaymentConfirmModal = ({
  open,
  onClose,
  onConfirm,
  control,
  setValue,
  cartSummary,
  formatInteger,
  isSubmitting,
  splitLines,
  setSplitLines,
}) => {
  const { t } = useTranslation();

  const totalUsedPoint = cartSummary?.totalUsedPoint || 0;
  // 총 결제금액에서 사용 포인트를 차감한, 실제 결제수단으로 청구될 금액
  const netTotalFinalPrice = (cartSummary?.totalFinalPrice || 0) - totalUsedPoint;

  // 카트를 고쳐 총액이 바뀌면 이미 입력한 라인은 의미가 없다 — 모달을 열 때마다 총액 기준으로 비운다.
  React.useEffect(() => {
    if (open) setSplitLines([]);
  }, [open, netTotalFinalPrice, setSplitLines]);

  // 결제는 항상 수납 라인으로 받는다(단일 수단이면 라인 1건). 합계가 총 결제금액과 같아야 결제할 수 있다 — 서버도 동일 검증.
  const splitPaid = splitLines.reduce((sum, line) => sum + line.amount, 0);
  // 청구할 금액이 없으면(전액 미수·전액 포인트) 받을 것도 없다.
  const nothingToCharge = netTotalFinalPrice <= 0;
  const splitIncomplete = !nothingToCharge && splitPaid !== netTotalFinalPrice;
  // 현금영수증은 주문 레벨 정보라 라인이 아니라 모달에서 한 번만 입력받는다.
  const hasCashLikeLine = splitLines.some(line => line.method === 'CASH' || line.method === 'TRANSFER');
  // 현금·계좌이체 라인의 결제상세(승인번호·가린 전화번호) 표시용 — 폼 값을 그대로 읽는다
  const cashReceiptWatch = useWatch({
    control,
    name: ['approvalNumber', 'receiptYn', 'deductionType', 'issuePhoneNumber', 'issueBusinessNumber'],
  });
  const cashReceipt = {
    approvalNumber: cashReceiptWatch[0],
    receiptYn: cashReceiptWatch[1],
    deductionType: cashReceiptWatch[2],
    phoneNumber: cashReceiptWatch[3],
    businessNumber: cashReceiptWatch[4],
  };

  // 결제 완료를 눌렀는데 수단 입력이 안 끝났으면 막지 말고 '결제수단 추가'로 안내한다(스크롤+펄스).
  const [guideTick, setGuideTick] = useState(0);
  const handleConfirm = () => {
    if (splitIncomplete) {
      setGuideTick(tick => tick + 1);
      return;
    }
    onConfirm();
  };

  return (
    <StyledModal open={open} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{t('결제 정보 입력')}</ModalTitle>
          <CloseButton onClick={onClose}>
            <Close sx={{ width: '20px', height: '20px', color: '#666' }} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {/* 결제 요약 */}
          <SummarySection>
            <SummaryRow>
              <SummaryLabel>{t('상품 수량')}</SummaryLabel>
              <SummaryValue>{cartSummary?.totalCount || 0}개</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t('판매 금액')}</SummaryLabel>
              <SummaryValue>{formatInteger(cartSummary?.totalPrice || 0)}원</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t('할인 금액')}</SummaryLabel>
              <SummaryValue>-{formatInteger(cartSummary?.totalDiscount || 0)}원</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t('미수금')}</SummaryLabel>
              <SummaryValue style={{ color: '#F44336' }}>{formatInteger(cartSummary?.totalOutstanding || 0)}원</SummaryValue>
            </SummaryRow>
            {totalUsedPoint > 0 && (
              <SummaryRow>
                <SummaryLabel>{t('사용 포인트')}</SummaryLabel>
                <SummaryValue>-{formatInteger(totalUsedPoint)}P</SummaryValue>
              </SummaryRow>
            )}
            <SummaryRow style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E5E5' }}>
              <SummaryLabel style={{ fontWeight: 600, color: '#111' }}>{t('총 결제금액')}</SummaryLabel>
              <SummaryTotal>{formatInteger(netTotalFinalPrice)}원</SummaryTotal>
            </SummaryRow>
          </SummarySection>

          {/* 결제수단 — 항상 수납 라인으로 입력받는다(단일 수단이면 라인 1건) */}
          <ContentSection>
            <SplitPaymentSection
              plain
              totalAmount={netTotalFinalPrice}
              guideTick={guideTick}
              lines={splitLines}
              onChange={setSplitLines}
              formatInteger={formatInteger}
              cashReceipt={cashReceipt}
              // 현금영수증은 주문 레벨 1회 입력 — 현금/계좌이체를 입력 중이거나 이미 라인에 담겼으면
              // '결제수단 추가' 버튼 위에 노출한다.
              beforeAddButton={currentMethod =>
                (currentMethod === 'CASH' || currentMethod === 'TRANSFER' || hasCashLikeLine) && (
                  <Box style={{ margin: '16px 0 4px' }}>
                    <CashPaymentForm control={control} setValue={setValue} />
                  </Box>
                )
              }
            />
          </ContentSection>

          {/* 메모 */}
          <ContentSection style={{ marginTop: '20px' }}>
            <FormFieldGroup>
              <FormFieldLabel>{t('메모')}</FormFieldLabel>
              <FormInputTextArea
                control={control}
                minRows={3}
                style={memoTextAreaStyle}
                placeholder={t('메모를 입력해 주세요.')}
                name="memo"
              />
            </FormFieldGroup>
          </ContentSection>
        </ModalContent>

        <ModalFooter>
          <CancelButton onClick={onClose}>{t('취소')}</CancelButton>
          <ConfirmButton
            onClick={handleConfirm}
            disabled={isSubmitting}
            className={!splitIncomplete && !isSubmitting ? 'confirm-guide-pulse' : undefined}>
            {isSubmitting ? t('처리중...') : t('결제 완료')}
          </ConfirmButton>
        </ModalFooter>
      </ModalContainer>
    </StyledModal>
  );
};

export default PaymentConfirmModal;
